/**
 * import_tier uit tekeninganalyse — filter vóór import naar conceptvoorstel.
 */

export type ImportTier = 'kern' | 'aanbevolen' | 'aanvullend';

export type ImportFilterPreset = 'kern' | 'kern_aanbevolen' | 'alles';

export function normalizeImportTier(raw: unknown): ImportTier {
  const t = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (t === 'kern' || t === 'aanbevolen' || t === 'aanvullend') return t;
  return 'aanbevolen';
}

export function tierMatchesPreset(tier: ImportTier, preset: ImportFilterPreset): boolean {
  if (preset === 'alles') return true;
  if (preset === 'kern') return tier === 'kern';
  return tier === 'kern' || tier === 'aanbevolen';
}

/** Startselectie na nieuwe analyse: praktisch midden (kern + aanbevolen). */
export const DEFAULT_IMPORT_PRESET: ImportFilterPreset = 'kern_aanbevolen';

const PRESET_LABELS: Record<ImportFilterPreset, string> = {
  kern: 'Minimaal (kern)',
  kern_aanbevolen: 'Kern + aanbevolen',
  alles: 'Alles',
};

export function importPresetLabel(preset: ImportFilterPreset): string {
  return PRESET_LABELS[preset];
}

/** Korte uitleg bij filter (per klanttype). */
export function importFilterHintForCustomerType(customerTypeId: string): string {
  const id = customerTypeId.trim() || 'anders';
  const hints: Record<string, string> = {
    particulier:
      'Kern = grootste kostenposten voor jouw besluit; voeg aanbevolen toe voor een completer beeld vóór je offertes vergelijkt.',
    aannemer:
      'Kern = strak voor calculatie/uitvoering; kern+aanbevolen = gebruikelijk om mee te werken; alles incl. detail/meerwerk-inschatting.',
    werkvoorbereider:
      'Zelfde als aannemer: kern voor kernposten, uitbreiden naar aanbevolen voor inkoop en planning.',
    adviseur:
      'Kern = ontwerp- en scopehoofdlijnen; aanbevolen = installaties en afwerking die het ontwerp compleet maken.',
    projectontwikkelaar:
      'Kern = investeringsdrivers; aanbevolen = volledigere scope voor vergelijking tussen scenario’s.',
    vastgoedontwikkelaar:
      'Zelfde als projectontwikkelaar.',
    woningcorporatie:
      'Kern = standaardscope per eenheid; aanbevolen = portefeuille-vergelijkbare aanvulling.',
    vve: 'Kern = gedeelde hoofdposten; aanbevolen = overige gemeenschappelijke voorzieningen.',
    huizenflipper: 'Kern = rendementsdrivers (keuken/badkamer/grote verbouw); breid uit als je risico wilt dekken.',
    overheid: 'Kern = toetsbare hoofdposten; aanbevolen = verdere onderbouwing dossier.',
    bank_financier: 'Kern = scope- en risicodrivers; aanbevolen = bredere kostenstructuur (executive niveau).',
    anders: 'Kern = hoofdposten; kern+aanbevolen = standaard werkset; alles = inclusief detail.',
  };
  return hints[id] ?? hints.anders;
}

export function importTierBadgeClass(tier: ImportTier): string {
  if (tier === 'kern') return 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100';
  if (tier === 'aanbevolen') return 'bg-sky-500/15 text-sky-950 dark:text-sky-100';
  return 'bg-text-muted/15 text-text-muted';
}

export function importTierLabelNl(tier: ImportTier): string {
  if (tier === 'kern') return 'Kern';
  if (tier === 'aanbevolen') return 'Aanbevolen';
  return 'Aanvullend';
}
